import { StatType } from "./enums";

/** Fraction of natural SP consumed that converts to team-wide ultimate charge. */
export const NATURAL_SP_TO_ULTIMATE_RATIO = 0.065;

/** Default baseline for all stats (attributes and combat stats). */
export const DEFAULT_STATS: Readonly<Record<StatType, number>> = {
  [StatType.BASE_HP]: 0,
  [StatType.BASE_DEFENSE]: 0,
  [StatType.BASE_ATTACK]: 0,
  [StatType.FLAT_ATTACK]: 0,
  [StatType.ATTACK_BONUS]: 0,
  [StatType.STRENGTH]: 0,
  [StatType.STRENGTH_BONUS]: 0,
  [StatType.AGILITY]: 0,
  [StatType.AGILITY_BONUS]: 0,
  [StatType.INTELLECT]: 0,
  [StatType.INTELLECT_BONUS]: 0,
  [StatType.WILL]: 0,
  [StatType.WILL_BONUS]: 0,
  // Virtual aliases — never stored under these keys; loadout aggregator
  // translates them to the operator's actual main/secondary attribute.
  [StatType.MAIN_ATTRIBUTE]: 0,
  [StatType.SECONDARY_ATTRIBUTE]: 0,
  [StatType.CRITICAL_RATE]: 0.05,
  [StatType.CRITICAL_DAMAGE]: 0.5,
  [StatType.ARTS_INTENSITY]: 0,
  [StatType.PHYSICAL_RESISTANCE]: 0,
  [StatType.ARTS_RESISTANCE]: 0,
  [StatType.HEAT_RESISTANCE]: 0,
  [StatType.ELECTRIC_RESISTANCE]: 0,
  [StatType.CRYO_RESISTANCE]: 0,
  [StatType.NATURE_RESISTANCE]: 0,
  [StatType.AETHER_RESISTANCE]: 0,
  [StatType.TREATMENT_BONUS]: 0,
  [StatType.TREATMENT_RECEIVED_BONUS]: 0,
  [StatType.COMBO_SKILL_COOLDOWN_REDUCTION]: 0,
  [StatType.ULTIMATE_GAIN_EFFICIENCY]: 0,
  [StatType.STAGGER_EFFICIENCY_BONUS]: 0,
  [StatType.PHYSICAL_DAMAGE_BONUS]: 0,
  [StatType.HEAT_DAMAGE_BONUS]: 0,
  [StatType.ELECTRIC_DAMAGE_BONUS]: 0,
  [StatType.CRYO_DAMAGE_BONUS]: 0,
  [StatType.NATURE_DAMAGE_BONUS]: 0,
  [StatType.BASIC_ATTACK_DAMAGE_BONUS]: 0,
  [StatType.BATTLE_SKILL_DAMAGE_BONUS]: 0,
  [StatType.COMBO_SKILL_DAMAGE_BONUS]: 0,
  [StatType.ULTIMATE_DAMAGE_BONUS]: 0,
  [StatType.STAGGER_DAMAGE_BONUS]: 0,
  [StatType.FINAL_STRIKE_DAMAGE_BONUS]: 0,
  [StatType.FINAL_DAMAGE_REDUCTION]: 0,
  [StatType.SKILL_DAMAGE_BONUS]: 0,
  [StatType.ARTS_DAMAGE_BONUS]: 0,
  [StatType.BASIC_ATTACK_PHYSICAL_DAMAGE_BONUS]: 0,
  [StatType.BASIC_ATTACK_HEAT_DAMAGE_BONUS]: 0,
  [StatType.BASIC_ATTACK_CRYO_DAMAGE_BONUS]: 0,
  [StatType.BASIC_ATTACK_NATURE_DAMAGE_BONUS]: 0,
  [StatType.BASIC_ATTACK_ELECTRIC_DAMAGE_BONUS]: 0,
  [StatType.BASIC_ATTACK_ARTS_DAMAGE_BONUS]: 0,
  [StatType.BATTLE_SKILL_PHYSICAL_DAMAGE_BONUS]: 0,
  [StatType.BATTLE_SKILL_HEAT_DAMAGE_BONUS]: 0,
  [StatType.BATTLE_SKILL_CRYO_DAMAGE_BONUS]: 0,
  [StatType.BATTLE_SKILL_NATURE_DAMAGE_BONUS]: 0,
  [StatType.BATTLE_SKILL_ELECTRIC_DAMAGE_BONUS]: 0,
  [StatType.BATTLE_SKILL_ARTS_DAMAGE_BONUS]: 0,
  [StatType.COMBO_SKILL_PHYSICAL_DAMAGE_BONUS]: 0,
  [StatType.COMBO_SKILL_HEAT_DAMAGE_BONUS]: 0,
  [StatType.COMBO_SKILL_CRYO_DAMAGE_BONUS]: 0,
  [StatType.COMBO_SKILL_NATURE_DAMAGE_BONUS]: 0,
  [StatType.COMBO_SKILL_ELECTRIC_DAMAGE_BONUS]: 0,
  [StatType.COMBO_SKILL_ARTS_DAMAGE_BONUS]: 0,
  [StatType.ULTIMATE_PHYSICAL_DAMAGE_BONUS]: 0,
  [StatType.ULTIMATE_HEAT_DAMAGE_BONUS]: 0,
  [StatType.ULTIMATE_CRYO_DAMAGE_BONUS]: 0,
  [StatType.ULTIMATE_NATURE_DAMAGE_BONUS]: 0,
  [StatType.ULTIMATE_ELECTRIC_DAMAGE_BONUS]: 0,
  [StatType.ULTIMATE_ARTS_DAMAGE_BONUS]: 0,
  [StatType.FINAL_STRIKE_PHYSICAL_DAMAGE_BONUS]: 0,
  [StatType.FINAL_STRIKE_HEAT_DAMAGE_BONUS]: 0,
  [StatType.FINAL_STRIKE_CRYO_DAMAGE_BONUS]: 0,
  [StatType.FINAL_STRIKE_NATURE_DAMAGE_BONUS]: 0,
  [StatType.FINAL_STRIKE_ELECTRIC_DAMAGE_BONUS]: 0,
  [StatType.FINAL_STRIKE_ARTS_DAMAGE_BONUS]: 0,
  [StatType.HP_BONUS]: 0,
  [StatType.FLAT_HP]: 0,
  // ── Damage factor stats ──────────────────────────────────────────────────────
  [StatType.DAMAGE_BONUS]: 0,
  [StatType.DAMAGE_TAKEN_BONUS]: 0,
  [StatType.PHYSICAL_AMP]: 0,
  [StatType.HEAT_AMP]: 0,
  [StatType.CRYO_AMP]: 0,
  [StatType.NATURE_AMP]: 0,
  [StatType.ELECTRIC_AMP]: 0,
  [StatType.ARTS_AMP]: 0,
  [StatType.HEAT_SUSCEPTIBILITY]: 0,
  [StatType.CRYO_SUSCEPTIBILITY]: 0,
  [StatType.NATURE_SUSCEPTIBILITY]: 0,
  [StatType.ELECTRIC_SUSCEPTIBILITY]: 0,
  [StatType.PHYSICAL_SUSCEPTIBILITY]: 0,
  [StatType.ARTS_SUSCEPTIBILITY]: 0,
  [StatType.PHYSICAL_FRAGILITY]: 0,
  [StatType.HEAT_FRAGILITY]: 0,
  [StatType.CRYO_FRAGILITY]: 0,
  [StatType.NATURE_FRAGILITY]: 0,
  [StatType.ELECTRIC_FRAGILITY]: 0,
  [StatType.ARTS_FRAGILITY]: 0,
  [StatType.PHYSICAL_RESISTANCE_IGNORE]: 0,
  [StatType.HEAT_RESISTANCE_IGNORE]: 0,
  [StatType.CRYO_RESISTANCE_IGNORE]: 0,
  [StatType.NATURE_RESISTANCE_IGNORE]: 0,
  [StatType.ELECTRIC_RESISTANCE_IGNORE]: 0,
  [StatType.ARTS_RESISTANCE_IGNORE]: 0,
  [StatType.PHYSICAL_RESISTANCE_REDUCTION]: 0,
  [StatType.HEAT_RESISTANCE_REDUCTION]: 0,
  [StatType.CRYO_RESISTANCE_REDUCTION]: 0,
  [StatType.NATURE_RESISTANCE_REDUCTION]: 0,
  [StatType.ELECTRIC_RESISTANCE_REDUCTION]: 0,
  [StatType.ARTS_RESISTANCE_REDUCTION]: 0,
  // ── Enemy stats ──────────────────────────────────────────────────────────────
  [StatType.STAGGER_HP]: 0,
  [StatType.STAGGER_RECOVERY]: 0,
  [StatType.FINISHER_ATK_MULTIPLIER]: 0,
  [StatType.FINISHER_SP_GAIN]: 0,
  [StatType.ATTACK_RANGE]: 0,
  [StatType.WEIGHT]: 0,
  // WEAKNESS stat starts at 1 (identity for multiplicative stacking): the enemy's
  // damage-output multiplier. Each APPLY STAT WEAKNESS uses with.multiplier which
  // multiplies this value (e.g. 0.8 × 0.8 = 0.64 for two 20% weakness sources).
  [StatType.WEAKNESS]: 1,
  [StatType.SHIELD]: 0,
  [StatType.SLOW]: 0,
  [StatType.STAGGER_FRAILTY]: 0,
};
